import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Users, WifiOff, Sparkles, Landmark, Zap, Languages } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n";
import { useMarket, priceLabel } from "@/market";

const included = [
  "Dossiers de chantier illimités",
  "Assistant vocal (dictée transformée en devis)",
  "Lecture de plaque signalétique par photo",
  "Photos classées, mesures, checklists métier",
  "Liste de matériel à acheter",
  "Rapport de visite et rapport d'intervention signés",
  "Devis PDF à votre en-tête, envoi et signature en ligne",
  "Relecture du devis par l'IA avant envoi",
  "Factures d'acompte, factures finales et notes de crédit",
  "Suivi des règlements et relances automatiques des impayés",
  "Passeport technique des équipements avec QR code et rappels d'entretien",
  "Suivi du chiffre d'affaires et des marges",
  "Mode hors ligne sur chantier",
  "__formats__",
  "Comptes de vos ouvriers inclus, sans supplément",
  "Sans engagement, résiliable à tout moment",
];

const arguments_ = [
  { icon: Users, title: "Un prix par société", line: "Invitez vos ouvriers : chacun a son accès, vous payez une seule fois." },
  { icon: Sparkles, title: "30 jours pour juger", line: "Testez sur vos vrais chantiers, sans carte bancaire." },
  { icon: WifiOff, title: "Aucun risque", line: "Pas d'engagement : vous arrêtez quand vous voulez." },
];

const faq = (price, country, t) => [
  { q: "Que se passe-t-il après les 30 jours ?", a: t("Vous choisissez de continuer pour {price}/mois. Sans action de votre part, vos dossiers restent accessibles en lecture, rien n'est supprimé.").replace("{price}", price) },
  { q: "Le prix est-il par utilisateur ?", a: t("Non. {price}/mois couvrent toute votre société : vous invitez vos ouvriers sans supplément.").replace("{price}", price) },
  { q: "TVA comprise ?", a: t(country === "BE" ? "Les {price} sont hors TVA. La TVA belge est ajoutée sur la facture." : "Les {price} sont hors TVA. La TVA applicable à votre pays est ajoutée sur la facture, ou autoliquidée si vous êtes assujetti.").replace("{price}", price) },
  { q: "Puis-je arrêter quand je veux ?", a: "Oui, l'abonnement est mensuel et résiliable à tout moment depuis votre espace." },
];

export default function TarifsPage() {
  const { t, lang } = useLanguage();
  const { market, country, countries, setCountry, copy } = useMarket();
  const price = priceLabel(market);
  const faqItems = faq(price, country, t);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = lang === "nl" ? "Tarieven | Devisto Studio" : "Tarifs | Devisto Studio";
    const meta = document.querySelector('meta[name="description"]');
    const previousMeta = meta?.getAttribute("content");
    meta?.setAttribute("content", lang === "nl"
      ? "Devisto Studio: 30 dagen gratis proberen, daarna € 25/maand per bedrijf — al uw werknemers inbegrepen."
      : "Devisto Studio : 30 jours d'essai gratuit, puis 25 €/mois par société — tous vos ouvriers inclus.");
    return () => {
      document.title = previousTitle;
      if (meta && previousMeta) meta.setAttribute("content", previousMeta);
    };
  }, [lang]);

  return (
    <div data-testid="pricing-page" className="font-body bg-[#F1F5F9] text-[#0F2645] min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 sm:py-24">
          <Reveal>
            <div className="max-w-2xl">
              <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#3A5CC8]">{t("Tarif")}</p>
              <h1 data-testid="pricing-title" className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F2645]">
                {t("Un seul prix, toute votre entreprise.")}
              </h1>
              <p className="mt-5 text-base leading-relaxed text-[#5C6A87]">
                {t("30 jours d'essai gratuit, sans carte bancaire. Ensuite")} <span data-testid="pricing-intro-price" className="font-bold text-[#0F2645]">{price}</span> {t("par mois pour votre société, ouvriers inclus.")}
              </p>
              <div data-testid="pricing-country-tabs" className="mt-6 flex flex-wrap gap-2" role="group" aria-label={t("Pays")}>
                {countries.map((c) => (
                  <button key={c.country} type="button" data-testid={`pricing-country-${c.country}`} aria-pressed={c.country === country} onClick={() => setCountry(c.country)}
                    className={`inline-flex min-h-[38px] items-center gap-1.5 rounded-full border px-3 text-xs font-bold transition-colors ${c.country === country ? "border-[#0F2645] bg-[#0F2645] text-white" : "border-[#E2E7F7] bg-white text-[#0F2645] hover:border-[#3A5CC8]"}`}>
                    <span aria-hidden="true">{c.flag}</span> {t(c.name)}
                  </button>
                ))}
              </div>
              <div data-testid="pricing-belgian-strip" className="mt-5 flex flex-wrap gap-2.5">
                {[
                  { icon: Landmark, label: copy.reassurance.title },
                  { icon: Zap, label: copy.einvoice.title },
                  { icon: Languages, label: copy.chips[copy.chips.length - 1] },
                ].map((b) => (
                  <span key={b.label} className="inline-flex items-center gap-2 rounded-full border border-[#E2E7F7] bg-white px-4 py-2 text-xs font-bold text-[#0F2645]">
                    <b.icon className="w-3.5 h-3.5 text-[#3A5CC8]" /> {t(b.label)}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6 items-stretch">
            <Reveal>
              <article data-testid="pricing-studio" className="h-full rounded-3xl bg-[#0F2645] p-9 text-white shadow-[0_20px_50px_rgba(11,30,61,0.18)] flex flex-col">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-300">Devisto Studio</p>
                <p className="mt-6 flex items-baseline gap-2">
                  <span data-testid="pricing-amount" className="font-heading text-5xl font-extrabold tracking-tight">{price}</span>
                  <span className="text-sm text-blue-100/80">{t("/ mois par société")}</span>
                </p>
                <p className="mt-3 text-sm text-blue-100/80">{t("HTVA · 30 jours d'essai · sans engagement")} · <span data-testid="pricing-country-label">{market.flag} {t(market.name)}</span></p>
                <div className="mt-8 space-y-4 flex-1">
                  {arguments_.map((a) => (
                    <div key={a.title} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/10 text-blue-200 shrink-0">
                        <a.icon className="w-4 h-4" />
                      </span>
                      <div>
                        <p className="font-heading text-sm font-semibold text-white">{t(a.title)}</p>
                        <p className="mt-1 text-sm text-blue-100/80 leading-relaxed">{t(a.line)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/studio/inscription"
                  data-testid="pricing-studio-cta"
                  className="mt-9 inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[#0F2645] hover:bg-[#EEF1FC] transition-colors duration-200"
                >
                  {t("Démarrer mon essai")} <ArrowRight className="w-4 h-4" />
                </Link>
              </article>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-[#E2E7F7] bg-white p-9 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
                <h2 className="font-heading text-lg font-semibold text-[#0F2645]">{t("Tout est inclus")}</h2>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {included.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#10B981]/10 shrink-0">
                        <Check className="w-3 h-3 text-[#10B981]" strokeWidth={3} />
                      </span>
                      <span className="text-sm leading-relaxed text-[#5C6A87]">{f === "__formats__" ? `${t("Export pour votre comptable :")} ${copy.formats}` : t(f)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
            {faqItems.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.06}>
                <div data-testid={`pricing-faq-${i}`} className="rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
                  <h3 className="font-heading text-base font-semibold text-[#0F2645]">{t(item.q)}</h3>
                  <p className="mt-2 text-sm text-[#5C6A87] leading-relaxed">{t(item.a)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
